import { segmentRectangleDistance, clearLineOfSight, GEOMETRY_EPSILON } from '../navigation/geometry.js';
import { terrainHeight, terrainLatticeHeight, terrainMesh } from './terrain.js';
import type { MapDefinition, Obstacle, Point } from './world-types.js';

/** Ground steeper than this (40°) cannot be walked: it is a cliff, wherever the terrain makes it. Ramps stay at or below it. */
export const MAX_WALKABLE_SLOPE = Math.tan(40 * Math.PI / 180);
/** Only cliffs at least this tall hide anything, and only from viewers this far below their rim. */
export const CLIFF_SIGHT_STEP = 1.5;
/** Melee cannot reach a target standing this much higher or lower. */
export const MELEE_HEIGHT_REACH = 2;

/** Whether either triangle of a grid cell is too steep to walk; the triangles are those of the ground mesh. */
export function steepCell(a: number, b: number, c: number, d: number, dx: number, dz: number) {
  // a: (x0, z0), b: (x0 + dx, z0), c: (x0, z0 + dz), d: (x0 + dx, z0 + dz); the mesh splits each cell along b–c.
  return Math.hypot((b - a) / dx, (c - a) / dz) > MAX_WALKABLE_SLOPE || Math.hypot((d - c) / dx, (d - b) / dz) > MAX_WALKABLE_SLOPE;
}

/**
 * Every unwalkable stretch of terrain becomes `'cliff'` obstacles: runs of steep grid cells, merged into rectangles across rows,
 * so navigation, collision, sight and the client all agree on where the ground cannot be crossed. Each keeps its rim elevation.
 */
export function cliffObstacles(map: MapDefinition): Obstacle[] {
  if (!map.terrain) return [];
  const mesh = terrainMesh(map), { columns, rows, dx, dz, tile, ratio } = mesh;
  // Each mesh cell (fine, or coarse over several lattice cells) is tested once, with the mesh's own corner heights.
  const steep = new Uint8Array(columns * rows), bounds = new Map<number, [rim: number, foot: number]>();
  for (let tileRow = 0; tileRow < mesh.tileRows; tileRow++) for (let tileColumn = 0; tileColumn < mesh.tileColumns; tileColumn++) {
    const step = mesh.fine[tileRow * mesh.tileColumns + tileColumn] ? 1 : ratio, i0 = tileColumn * tile, j0 = tileRow * tile;
    const i1 = Math.min(columns, i0 + tile), j1 = Math.min(rows, j0 + tile), span = (i1 - i0) / step + 1;
    const heights = new Float64Array(span * ((j1 - j0) / step + 1));
    for (let j = j0; j <= j1; j += step) for (let i = i0; i <= i1; i += step) heights[(j - j0) / step * span + (i - i0) / step] = terrainLatticeHeight(map, i, j);
    for (let j = j0; j < j1; j += step) for (let i = i0; i < i1; i += step) {
      const at = (j - j0) / step * span + (i - i0) / step, a = heights[at], b = heights[at + 1], c = heights[at + span], d = heights[at + span + 1];
      if (!steepCell(a, b, c, d, dx * step, dz * step)) continue;
      const rim = Math.max(a, b, c, d), foot = Math.min(a, b, c, d);
      for (let row = j; row < j + step; row++) for (let column = i; column < i + step; column++) { steep[row * columns + column] = 1; bounds.set(row * columns + column, [rim, foot]); }
    }
  }
  // Runs of steep cells along each row, merged with identical runs in the rows below.
  type Run = { start: number; end: number; firstRow: number; rim: number; foot: number };
  const obstacles: Obstacle[] = [], close = (run: Run, lastRow: number) => obstacles.push({ kind: 'cliff',
    x: (run.start + run.end + 1) / 2 * dx - map.width / 2, z: (run.firstRow + lastRow + 1) / 2 * dz - map.depth / 2,
    width: (run.end - run.start + 1) * dx, depth: (lastRow - run.firstRow + 1) * dz, height: run.rim - run.foot, rim: run.rim });
  let open = new Map<string, Run>();
  for (let row = 0; row < rows; row++) {
    const next = new Map<string, Run>();
    let start = -1, rim = -Infinity, foot = Infinity;
    for (let column = 0; column <= columns; column++) {
      const cell = row * columns + column, isSteep = column < columns && steep[cell] === 1;
      if (isSteep) { const [top, bottom] = bounds.get(cell)!; rim = Math.max(rim, top); foot = Math.min(foot, bottom); if (start < 0) start = column; }
      if (!isSteep && start >= 0) {
        const key = `${start}:${column - 1}`, above = open.get(key);
        if (above) { above.rim = Math.max(above.rim, rim); above.foot = Math.min(above.foot, foot); open.delete(key); next.set(key, above); }
        else next.set(key, { start, end: column - 1, firstRow: row, rim, foot });
        start = -1; rim = -Infinity; foot = Infinity;
      }
    }
    for (const run of open.values()) close(run, row - 1);
    open = next;
  }
  for (const run of open.values()) close(run, rows - 1);
  return obstacles;
}

/** A cliff hides what lies beyond it from a viewer below its rim; from on top you look down past it. Low banks hide nothing. */
export function cliffHides(cliff: Obstacle, viewerElevation: number) {
  return (cliff.height ?? 0) >= CLIFF_SIGHT_STEP && viewerElevation < (cliff.rim ?? 0) - CLIFF_SIGHT_STEP;
}

const split = new WeakMap<readonly Obstacle[], { others: Obstacle[]; cliffs: Obstacle[] }>();
/** The obstacles split into cliffs and everything else, cached per obstacle list so the sight grid is built once. */
export function cliffsAmong(obstacles: readonly Obstacle[]) {
  let parts = split.get(obstacles);
  if (!parts) split.set(obstacles, parts = { others: obstacles.filter(o => o.kind !== 'cliff'), cliffs: obstacles.filter(o => o.kind === 'cliff') });
  return parts;
}

/** Combat line of sight: solid scenery always blocks; cliffs block only when the viewer looks up at them. */
export function terrainLineOfSight(map: Pick<MapDefinition, 'obstacles'> & Partial<MapDefinition>, viewer: Point, target: Point) {
  const { others, cliffs } = cliffsAmong(map.obstacles);
  if (!cliffs.length) return clearLineOfSight(map.obstacles, viewer, target);
  if (!clearLineOfSight(others, viewer, target)) return false;
  if (clearLineOfSight(cliffs, viewer, target)) return true;
  const eye = map.terrain ? terrainHeight(map as MapDefinition, viewer.x, viewer.z) : 0;
  return !cliffs.some(cliff => cliffHides(cliff, eye) && segmentRectangleDistance(viewer, target, cliff) <= GEOMETRY_EPSILON);
}
